import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { useNotify, useDataProvider, useGetList } from 'react-admin';
import CircularProgress from '@material-ui/core/CircularProgress';
import { useBrsCreateStyles } from './BrsCreateStyles';
import { NewRequestSection } from './components/NewRequestSection';
import { BroadcastRequestsSection } from './components/BroadcastRequestsSection';
import { DestinationsSection } from './components/DestinationsSection';
import { FormFooter } from './components/FormFooter';
import { WeeklyRotationSummary } from './components/WeeklyRotationSummary';
import { scheduleToDayRotations, computeDailyTotals, DAYS } from './components/splitWeekUtils';
import { validateOriginalsPresent } from './brsValidation';
import PDFPreviewModal from '../../components/PDF/PDFPreviewModal';

let nextSpotId = 1;

// Surface the most informative error string from an axios failure. The BRS
// routes return FastAPI HTTPException bodies of shape {detail: <string>},
// 400 for known validation prefixes and 500 otherwise (commit f3e1dc97).
// Falling back to error.message keeps network/timeout errors readable.
const extractApiErrorMessage = (error) => {
  const detail = error?.response?.data?.detail;
  if (typeof detail === 'string' && detail.length > 0) return detail;
  return error?.message || 'Unknown error';
};

// Phase 2 draft persistence — frontend-only. Drafts live in localStorage under
// a single key and include the full form state (formData, spots,
// selectedDestinations). Each draft is keyed by estimate_id so reopening the
// same estimate restores your in-progress work. Drafts are wiped after a
// successful submit and can be dismissed without restoring via the banner.
// TODO(backend): once a draft endpoint exists, swap `readDraft`/`writeDraft`
// for server calls — the shape stored here is self-contained and should
// transfer directly.
const DRAFT_STORAGE_KEY = 'brsCreateDrafts.v1';
const ANON_DRAFT_KEY = '__anonymous__';

const readAllDrafts = () => {
  try {
    const raw = localStorage.getItem(DRAFT_STORAGE_KEY);
    return raw ? JSON.parse(raw) : {};
  } catch (err) {
    console.warn('Failed to read BRS drafts from localStorage:', err);
    return {};
  }
};

const writeAllDrafts = (drafts) => {
  try {
    localStorage.setItem(DRAFT_STORAGE_KEY, JSON.stringify(drafts));
  } catch (err) {
    console.warn('Failed to persist BRS drafts to localStorage:', err);
  }
};

const draftKeyFor = (estimateId) =>
  estimateId && estimateId.trim() ? estimateId.trim() : ANON_DRAFT_KEY;

const saveDraftSnapshot = (formData, spots, selectedDestinations) => {
  const drafts = readAllDrafts();
  drafts[draftKeyFor(formData.estimate_id)] = {
    savedAt: new Date().toISOString(),
    formData,
    spots,
    selectedDestinations,
  };
  writeAllDrafts(drafts);
};

const loadDraftSnapshot = (estimateId) => {
  const drafts = readAllDrafts();
  return drafts[draftKeyFor(estimateId)] || null;
};

const deleteDraftSnapshot = (estimateId) => {
  const drafts = readAllDrafts();
  delete drafts[draftKeyFor(estimateId)];
  writeAllDrafts(drafts);
};

const createSpot = (overrides = {}, scheduleDefault = 100) => ({
  _id: nextSpotId++,
  isciasset: {
    isci: '',
    sigma_encoding: 'CADENT',
    bvs_encoding: 'NO',
    include_sd: false,
    duration: 0,
    original_iscis: [{}],
  },
  title: '',
  start_date: '',
  end_date: '',
  unit: '',
  rotation: '',
  // BRS-FORM-P1 Phase 1: per-spot smart-form flags forwarded to the
  // backend's BroadcastCreateRequest (a3e2add8). `traffic_only` flips to
  // true when the ISCI resolves to an existing Cadent master; `copy_id`
  // echoes the ISCI as the Cadent Copy ID reference.
  traffic_only: false,
  copy_id: null,
  // Phase 2: split-week schedule. Default depends on unit_rotation mode:
  // 100 (uniform rotation %) for rotation mode, 1 (one unit) for unit mode.
  split_week_schedule: {
    mon: scheduleDefault,
    tue: scheduleDefault,
    wed: scheduleDefault,
    thu: scheduleDefault,
    fri: scheduleDefault,
    sat: scheduleDefault,
    sun: scheduleDefault,
  },
  ...overrides,
});

export const BrsCreate = (props) => {
  const classes = useBrsCreateStyles();
  const notify = useNotify();
  const dataProvider = useDataProvider();

  // Form state
  const [formData, setFormData] = useState({
    request_id: '',
    estimate_id: '',
    slate_type: 'CADENT',
    market_type: 'LOCAL',
    inventory_source_id: '',
    unit_rotation: 'unit',
    // Flight window (commit bc8f53a4): operator-supplied broadcast-week window
    // that drives the traffic-instructions PDF header. flight_start_date is a
    // Monday, flight_end_date a Sunday (UI-only, recomputed from the others),
    // flight_weeks a positive integer. start_date + weeks are sent on the wire.
    flight_start_date: '',
    flight_end_date: '',
    flight_weeks: '',
  });

  const [spots, setSpots] = useState([createSpot({}, 1)]);
  const [selectedDestinations, setSelectedDestinations] = useState([]);
  const [antennaSDActive, setAntennaSDActive] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [brandChoices, setBrandChoices] = useState([]);

  // Zero-day acknowledgements (BRS split-week). Holds the day keys
  // ('mon'..'sun') whose 0 daily total the operator has explicitly confirmed
  // by clicking the day's "Daily Total" cell. A 0-total day blocks submission
  // until it appears here. Applies to both Rotation and Unit modes.
  const [acknowledgedZeroDays, setAcknowledgedZeroDays] = useState([]);

  // Draft restore banner: when a draft exists for the anonymous slot on
  // mount (or later for a typed estimate_id), surface it so the operator
  // can restore or dismiss. We don't auto-restore because that would
  // silently overwrite a fresh form.
  const [availableDraft, setAvailableDraft] = useState(null);

  // Mirror `formData` into a ref so async callbacks can read the freshest
  // typed value without forcing themselves to re-create on every keystroke.
  // The Estimate ID blur handler in particular needs this: it fires the
  // moment the input loses focus, *after* the user's last onChange has
  // scheduled a setFormData update, and the previous implementation tried
  // to read that pending value back through `setFormData((prev) => ...)`.
  // That trick only works when React's useState eager-state path runs
  // (queue is empty, no pending updates) — when blur fires right after a
  // keystroke, the queued update keeps the eager path off and the inline
  // `estimateId` stayed undefined, so the handler early-returned and the
  // destination chips never populated. The ref is updated in an effect
  // after every render so it's always current by the time the next event
  // handler reads it.
  const formDataRef = useRef(formData);
  useEffect(() => {
    formDataRef.current = formData;
  });

  // Inventory sources
  const { data: inventorySourceData } = useGetList(
    'inventorysource',
    { page: 1, perPage: 500 },
    { field: 'name', order: 'ASC' },
  );
  const inventorySources = Object.values(inventorySourceData || {});

  // PDF preview state (preserve existing workflow)
  const [pdfModalOpen, setPdfModalOpen] = useState(false);
  const [pdfUrl, setPdfUrl] = useState('');
  const [trafficInstructionsKey, setTrafficInstructionsKey] = useState('');
  const [pdfFileName, setPdfFileName] = useState('');
  const [savedFormData, setSavedFormData] = useState(null);

  // Manual PDF attachment (parity with the main-branch ReviewToolbar in
  // commit 8d6b5440). Operators can skip the Generate-PDF preview flow and
  // upload a finished traffic-instructions PDF directly; the submit handler
  // POSTs the file to /upload-traffic-instructions and forwards the
  // returned key to /api/v1/brs-create.
  const [attachedPdfFile, setAttachedPdfFile] = useState(null);

  // Load brand choices
  useEffect(() => {
    dataProvider
      .getList('brand', { pagination: { page: 1, perPage: 500 }, sort: { field: 'name', order: 'ASC' } })
      .then(({ data }) => {
        setBrandChoices(data.sort((a, b) => a.name.localeCompare(b.name)));
      })
      .catch(console.error);
  }, [dataProvider]);

  // On mount, look for a draft saved under the anonymous slot (no estimate_id
  // typed yet) and surface the restore banner if present.
  useEffect(() => {
    const draft = loadDraftSnapshot('');
    if (draft) setAvailableDraft(draft);
  }, []);

  // Antenna SD auto-toggle: when Antenna-SD is selected, enable SD on all spots
  const handleAntennaSDChange = useCallback((hasAntenna) => {
    setAntennaSDActive(hasAntenna);
    if (hasAntenna) {
      setSpots((prev) =>
        prev.map((spot) => ({
          ...spot,
          isciasset: { ...spot.isciasset, include_sd: true },
        })),
      );
    }
  }, []);

  // Estimate ID blur handler: pre-populate destinations from the last
  // submitted job for this estimate. We intentionally do NOT pre-populate
  // spots/ISCIs — operators want the destinations carried forward but the
  // ISCI rows authored fresh per job.
  const handleEstimateIdBlur = useCallback(async () => {
    // Read the freshest typed estimate_id via the ref (see comment on
    // formDataRef above). Reading from the closure directly would pin us to
    // the initial render's value because this useCallback intentionally
    // omits formData from its deps to stay stable across keystrokes.
    const estimateId = formDataRef.current.estimate_id;
    if (!estimateId) return;

    try {
      const { data } = await dataProvider.getList('brsjob', {
        filter: {
          'estimate_id@_eq': estimateId,
          'destinations#id@_is_null': false,
        },
        sort: { field: 'created', order: 'DESC' },
        pagination: { page: 1, perPage: 1 },
      });

      if (data.length === 0) return;
      const lastJob = data[0];

      const { data: destData } = await dataProvider.getManyReference('brsjobdestination', {
        target: 'brs_job_id',
        id: lastJob.id,
        sort: { field: 'created', order: 'DESC' },
        pagination: { page: 1, perPage: 500 },
      });

      if (destData && destData.length > 0) {
        const mapped = destData.map((d) => ({
          id: d.destination.id,
          name: d.destination.name,
          call_letters: d.destination.call_letters,
        }));
        setSelectedDestinations(mapped);
      }
    } catch (err) {
      console.error('Failed to pre-populate destinations from last job:', err);
    }
  }, [dataProvider]);

  // Form field change handler using functional updates to avoid stale closures
  const handleFormDataChange = useCallback((newFormData) => {
    setFormData((prevFormData) => {
      const slateChanged = newFormData.slate_type !== prevFormData.slate_type;
      const modeChanged = newFormData.unit_rotation !== prevFormData.unit_rotation;

      // When the unit/rotation mode toggles, reset every spot's schedule to
      // the new default (1 for unit, 100 for rotation) so operators start
      // fresh in the new mode instead of seeing stale percentage or unit
      // values that don't make sense.
      if (modeChanged) {
        const newDefault = newFormData.unit_rotation === 'unit' ? 1 : 100;
        setSpots((prev) =>
          prev.map((spot) => ({
            ...spot,
            split_week_schedule: {
              mon: newDefault,
              tue: newDefault,
              wed: newDefault,
              thu: newDefault,
              fri: newDefault,
              sat: newDefault,
              sun: newDefault,
            },
          })),
        );
      }

      if (slateChanged) {
        const isFluidity = newFormData.slate_type === 'FLUIDITY';
        setSpots((prev) =>
          prev.map((spot) => ({
            ...spot,
            isciasset: {
              ...spot.isciasset,
              sigma_encoding: isFluidity ? 'NONE' : 'CADENT',
              include_sd: isFluidity ? false : spot.isciasset.include_sd,
            },
          })),
        );
        if (isFluidity) {
          return {
            ...newFormData,
            request_id: newFormData.request_id || '0',
            estimate_id: newFormData.estimate_id || '0',
          };
        }
      }

      return newFormData;
    });
  }, []);

  // Build the payload in the same shape the backend expects
  const buildPayload = () => {
    const brsSpots = spots.map((spot) => {
      const result = {
        start_date: spot.start_date || null,
        end_date: spot.end_date || null,
        title: spot.title || '',
        // BRS-FORM-P1 Phase 1 smart-form fields (matches the
        // BroadcastCreateRequest additions in a3e2add8). `traffic_only`
        // tells the backend to skip media delivery for this spot; `copy_id`
        // is the Cadent Copy ID the ISCI resolved to.
        traffic_only: spot.traffic_only === true,
        copy_id: spot.copy_id || null,
        // Translate the UI schedule into the backend's DayRotationInput[]
        // shape. The PDF generator and BroadcastCreateRequest both consume
        // `day_rotations` exclusively (see distrohub/use_cases/brs/create.py
        // and pdf_generator.py); the `split_week_schedule` key is silently
        // ignored by Pydantic but kept on the wire so any preview/debug
        // tooling that still reads the legacy object shape continues to work.
        split_week_schedule: spot.split_week_schedule || {
          mon: 100,
          tue: 100,
          wed: 100,
          thu: 100,
          fri: 100,
          sat: 100,
          sun: 100,
        },
        day_rotations: scheduleToDayRotations(spot.split_week_schedule),
        isciasset: {
          isci: spot.isciasset?.isci || '',
          sigma_encoding: spot.isciasset?.sigma_encoding || 'CADENT',
          bvs_encoding: spot.isciasset?.bvs_encoding || 'NO',
          include_sd: spot.isciasset?.include_sd || false,
          duration: spot.isciasset?.duration || 0,
          original_iscis: (spot.isciasset?.original_iscis || []).filter((oi) => oi.isci),
        },
      };

      // Include per-spot unit/rotation so the traffic-update page can detect
      // which mode was used (the brsjob table may not have a unit_rotation
      // column). Also populates the PDF rotation column.
      result.unit = spot.unit || '';
      result.rotation = spot.rotation || '';

      if (spot.isciasset?.exists !== undefined) {
        result.isciasset.exists = spot.isciasset.exists;
      }

      if (spot.isPrePopulated) {
        result.is_traffic_update = true;
      }

      return result;
    });

    // Send the explicit flight window: flight_start_date (Monday, ISO or null)
    // and flight_weeks (positive int or null). flight_end_date is UI-only and
    // is dropped from the payload (the backend re-derives Sunday from the
    // start + week count). A non-positive week count is coerced to null since
    // the backend enforces conint(gt=0).
    const weeksNum = parseInt(formData.flight_weeks, 10);
    const payload = {
      ...formData,
      flight_start_date: formData.flight_start_date || null,
      flight_weeks: Number.isFinite(weeksNum) && weeksNum > 0 ? weeksNum : null,
      brs_spots: brsSpots,
      destinations_values: selectedDestinations.map((d) => d.id),
    };
    delete payload.flight_end_date;
    return payload;
  };

  // Mirror the backend's pre-write checks from
  // distrohub/use_cases/brs/create.py (commit 8cd77db8) so operators get
  // inline warnings instead of a 500 from the API:
  //   - DayRotationInput conint validation: 0 <= day_of_week <= 6,
  //     0 <= percent <= 100.
  //   - validate_day_rotation_totals: per-week (start_date, end_date) per-day
  //     totals across spots must not exceed 100%.
  // scheduleToDayRotations already snaps to {0,25,50,100} and 0..6 indices,
  // so the per-value checks here are a defensive safety net for malformed
  // restored drafts; the cross-spot rollup is the genuine new guard.
  const validateDayRotationTotals = (allSpots) => {
    // In unit mode the schedule values are absolute unit counts, not
    // percentages — the "≤ 100% per day" cross-spot constraint does not
    // apply. Skip validation entirely.
    if (formData.unit_rotation === 'unit') return null;

    const totals = {};
    for (const spot of allSpots) {
      const dayRotations = scheduleToDayRotations(spot.split_week_schedule);
      if (!dayRotations.length) continue;
      const weekKey = `${spot.start_date || ''}__${spot.end_date || ''}`;
      for (const dr of dayRotations) {
        if (dr.day_of_week < 0 || dr.day_of_week > 6) {
          return `day_of_week must be 0..6, got ${dr.day_of_week}`;
        }
        if (dr.percent < 0 || dr.percent > 100) {
          return `percent must be 0..100, got ${dr.percent}`;
        }
        const key = `${weekKey}__${dr.day_of_week}`;
        totals[key] = (totals[key] || 0) + dr.percent;
      }
    }
    for (const [key, total] of Object.entries(totals)) {
      if (total > 100) {
        const [start, end, day] = key.split('__');
        return `Day rotation total for week ${start}..${end} day ${day} is ${total}% (max 100%)`;
      }
    }
    return null;
  };

  // Validation
  const validate = () => {
    if (!formData.request_id?.trim()) {
      notify('Request ID is required', 'warning');
      return false;
    }
    if (!formData.estimate_id?.trim()) {
      notify('Estimate ID is required', 'warning');
      return false;
    }
    if (spots.length === 0) {
      notify('At least one broadcast request is required', 'warning');
      return false;
    }
    for (let i = 0; i < spots.length; i++) {
      if (!spots[i].isciasset?.isci) {
        notify(`ISCI is required for spot ${i + 1}`, 'warning');
        return false;
      }
      if (!spots[i].title?.trim()) {
        notify(`Title is required for spot ${i + 1}`, 'warning');
        return false;
      }
      // Mirror BrsTransferCreate.execute() pre-write check: a traffic-only
      // spot must carry the resolved Cadent Copy ID. The CatDV lookup in
      // IsciEntryCard.runIsciLookup populates copy_id when it flips
      // traffic_only=true, so this guard catches any drift (e.g. a stale
      // restored draft) before the backend does.
      if (spots[i].traffic_only && !spots[i].copy_id) {
        notify(`Spot ${i + 1}: traffic-only requires a Copy ID`, 'warning');
        return false;
      }
    }
    if (selectedDestinations.length === 0) {
      notify('Please add at least one destination', 'warning');
      return false;
    }
    const dayRotationError = validateDayRotationTotals(spots);
    if (dayRotationError) {
      notify(dayRotationError, 'warning');
      return false;
    }
    const originalsError = validateOriginalsPresent(spots);
    if (originalsError) {
      notify(originalsError, 'warning');
      return false;
    }
    return true;
  };

  // Manual PDF attachment: spawn a hidden <input type="file"> to grab a
  // single .pdf. Mirrors the main-branch ReviewToolbar.handleAttachPDF
  // (commit 8d6b5440) — we keep the dynamic-input approach so the button
  // doesn't need a permanent DOM element and we don't have to manage a ref
  // reset between picks.
  const handleAttachPdf = () => {
    const fileInput = document.createElement('input');
    fileInput.type = 'file';
    fileInput.accept = '.pdf';
    fileInput.style.display = 'none';
    document.body.appendChild(fileInput);
    fileInput.onchange = (event) => {
      const file = event.target.files?.[0];
      if (file) setAttachedPdfFile(file);
      document.body.removeChild(fileInput);
    };
    fileInput.click();
  };

  const handleRemoveAttachedPdf = () => {
    setAttachedPdfFile(null);
  };

  // Submit job. Three submission paths share this handler:
  //   1. A manually attached PDF (attachedPdfFile set): upload it to
  //      /upload-traffic-instructions first, then create the BRS job with
  //      the returned storage key. Matches the main-branch send flow.
  //   2. A previously generated PDF that the operator skipped sending
  //      through the preview modal (trafficInstructionsKey set): reuse the
  //      already-uploaded key.
  //   3. No PDF at all: send empty key/filename fields and let the backend
  //      record the job without a traffic-instructions document.
  const handleSubmit = async () => {
    if (!validate()) return;

    try {
      setIsSubmitting(true);
      const token = localStorage.getItem('token');
      const payload = buildPayload();

      let resolvedKey = trafficInstructionsKey || '';
      let resolvedFileName = pdfFileName || '';

      if (attachedPdfFile) {
        const uploadFormData = new FormData();
        uploadFormData.append('file', attachedPdfFile);
        const uploadResponse = await axios.post(
          `${window.REACT_APP_API_URL}/upload-traffic-instructions`,
          uploadFormData,
          {
            headers: {
              Authorization: `Bearer ${token}`,
              'Content-Type': 'multipart/form-data',
            },
          },
        );
        resolvedKey = uploadResponse.data?.key || '';
        resolvedFileName = attachedPdfFile.name;
      }

      payload.trafficinstructions_key = resolvedKey;
      payload.file_name = resolvedFileName;
      payload.filepath = resolvedKey;
      payload.traffic_instruction_version = 1;

      await axios.post(
        `${window.REACT_APP_API_URL}/api/v1/brs-create`,
        { brs_job: payload },
        { headers: { Authorization: `Bearer ${token}` } },
      );

      // Clean up the local draft for this estimate so the next visit is fresh.
      deleteDraftSnapshot(formData.estimate_id);
      notify('BRS job created successfully', 'success');
      window.location.href = '#/brs';
    } catch (error) {
      notify(`Error creating BRS job: ${extractApiErrorMessage(error)}`, 'error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSaveDraft = () => {
    saveDraftSnapshot(formData, spots, selectedDestinations);
    const label = formData.estimate_id
      ? `estimate ${formData.estimate_id}`
      : 'anonymous slot';
    notify(`Draft saved locally (${label})`, 'info');
  };

  const handleRestoreDraft = () => {
    if (!availableDraft) return;
    if (availableDraft.formData) setFormData(availableDraft.formData);
    if (Array.isArray(availableDraft.spots) && availableDraft.spots.length > 0) {
      setSpots(availableDraft.spots);
    }
    if (Array.isArray(availableDraft.selectedDestinations)) {
      setSelectedDestinations(availableDraft.selectedDestinations);
    }
    setAvailableDraft(null);
    notify('Draft restored', 'success');
  };

  const handleDismissDraft = () => {
    deleteDraftSnapshot(availableDraft?.formData?.estimate_id || '');
    setAvailableDraft(null);
  };

  const handleCancel = () => {
    window.location.href = '#/brs';
  };

  // Generate PDF preview
  const handleGeneratePdf = async () => {
    if (!validate()) return;

    try {
      setIsSubmitting(true);
      const payload = buildPayload();
      const token = localStorage.getItem('token');

      const response = await axios.post(
        `${window.REACT_APP_API_URL}/api/v1/generate-traffic-instructions`,
        { brs_job: payload },
        { headers: { Authorization: `Bearer ${token}` } },
      );

      if (response.data?.url) {
        setPdfUrl(response.data.url);
        setTrafficInstructionsKey(response.data.key);
        setPdfFileName(response.data.filename);
        setSavedFormData(payload);
        setPdfModalOpen(true);
      } else {
        notify('No PDF URL received', 'error');
      }
    } catch (error) {
      notify(`Error generating traffic instructions: ${extractApiErrorMessage(error)}`, 'error');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Handle PDF modal send - called from PDFPreviewModal's onClick (receives MouseEvent, not form data)
  // We use savedFormData which was captured during handleGeneratePdf
  const handlePdfSend = async () => {
    if (!savedFormData) {
      notify('No form data available. Please generate PDF first.', 'error');
      return;
    }

    try {
      setIsSubmitting(true);
      const token = localStorage.getItem('token');

      const dataToSend = {
        ...savedFormData,
        trafficinstructions_key: trafficInstructionsKey,
        file_name: pdfFileName,
        filepath: trafficInstructionsKey,
        traffic_instruction_version: 1,
      };

      await axios.post(
        `${window.REACT_APP_API_URL}/api/v1/brs-create`,
        { brs_job: dataToSend },
        { headers: { Authorization: `Bearer ${token}` } },
      );

      // Clean up the local draft for this estimate so the next visit is fresh.
      deleteDraftSnapshot(formData.estimate_id);
      setPdfModalOpen(false);
      notify('BRS job created successfully', 'success');
      window.location.href = '#/brs';
    } catch (error) {
      notify(`Error creating BRS job: ${extractApiErrorMessage(error)}`, 'error');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Submit gate (BRS split-week). In Rotation mode every day column must total
  // exactly 0% or 100% across all spots:
  //   - A total over 100% or a partial split (1..99%) blocks Generate PDF /
  //     Submit Job until the operator fixes it.
  //   - A total of exactly 0% (nothing airing) is allowed, but the operator
  //     must first click that day's "Daily Total" cell to confirm awareness;
  //     until then the buttons stay disabled.
  // In Unit mode the 100% constraint doesn't apply, but zero-day confirmation
  // is still required — operators must acknowledge days with 0 total units.
  const isRotationMode = formData.unit_rotation === 'rotation';
  const isUnitMode = formData.unit_rotation === 'unit';
  const dailyTotals = computeDailyTotals(spots);
  // zeroDaySig runs for both modes so zero-day acks are tracked regardless.
  const zeroDaySig = DAYS.filter((d) => dailyTotals[d.key] === 0)
    .map((d) => d.key)
    .join(',');

  // Drop acknowledgements for days that are no longer 0 so a day that leaves
  // and later re-enters 0 must be confirmed again.
  useEffect(() => {
    const stillZero = zeroDaySig.split(',').filter(Boolean);
    setAcknowledgedZeroDays((prev) => {
      const next = prev.filter((k) => stillZero.includes(k));
      return next.length === prev.length ? prev : next;
    });
  }, [zeroDaySig]);

  const toggleZeroDayAck = useCallback((dayKey) => {
    setAcknowledgedZeroDays((prev) =>
      prev.includes(dayKey) ? prev.filter((k) => k !== dayKey) : [...prev, dayKey],
    );
  }, []);

  let submitBlocked = false;
  let submitBlockedReason;
  if (isRotationMode) {
    const over100 = DAYS.filter((d) => dailyTotals[d.key] > 100);
    const partial = DAYS.filter((d) => dailyTotals[d.key] > 0 && dailyTotals[d.key] < 100);
    const unconfirmedZero = DAYS.filter(
      (d) => dailyTotals[d.key] === 0 && !acknowledgedZeroDays.includes(d.key),
    );
    if (over100.length > 0) {
      submitBlocked = true;
      submitBlockedReason = `Daily total exceeds 100% on ${over100
        .map((d) => d.label)
        .join(', ')}. Each day must total exactly 100%.`;
    } else if (partial.length > 0) {
      submitBlocked = true;
      submitBlockedReason = `Daily total must equal 100% on ${partial
        .map((d) => d.label)
        .join(', ')} (currently under 100%).`;
    } else if (unconfirmedZero.length > 0) {
      submitBlocked = true;
      const labels = unconfirmedZero.map((d) => d.label).join(', ');
      submitBlockedReason = `Click the 0% Daily Total for ${labels} to confirm ${
        unconfirmedZero.length > 1 ? 'those days are' : 'that day is'
      } intentionally empty.`;
    }
  } else if (isUnitMode) {
    const unconfirmedZero = DAYS.filter(
      (d) => dailyTotals[d.key] === 0 && !acknowledgedZeroDays.includes(d.key),
    );
    if (unconfirmedZero.length > 0) {
      submitBlocked = true;
      const labels = unconfirmedZero.map((d) => d.label).join(', ');
      submitBlockedReason = `Click the 0-unit Daily Total for ${labels} to confirm ${
        unconfirmedZero.length > 1 ? 'those days have' : 'that day has'
      } no units intentionally.`;
    }
  }
  if (!submitBlocked) {
    const originalsError = validateOriginalsPresent(spots);
    if (originalsError) {
      submitBlocked = true;
      submitBlockedReason = originalsError;
    }
  }

  return (
    <div className={classes.root}>
      <a className={classes.breadcrumbLink} href="#/brs">
        {'< BRS Jobs'}
      </a>

      <div className={classes.content}>
        {availableDraft && (
          <div className={classes.hybridBanner} style={{ justifyContent: 'space-between' }}>
            <span>
              &#9673; You have a locally saved draft from{' '}
              {new Date(availableDraft.savedAt).toLocaleString()}. Restore it?
            </span>
            <span style={{ display: 'flex', gap: 8 }}>
              <button
                type="button"
                className={`${classes.btn} ${classes.btnPrimary}`}
                onClick={handleRestoreDraft}>
                Restore draft
              </button>
              <button
                type="button"
                className={`${classes.btn} ${classes.btnGhost}`}
                onClick={handleDismissDraft}>
                Dismiss
              </button>
            </span>
          </div>
        )}
        <NewRequestSection
          formData={formData}
          onChange={handleFormDataChange}
          inventorySources={inventorySources}
          onEstimateIdBlur={handleEstimateIdBlur}
          showFlightWindow
        />

        <BroadcastRequestsSection
          spots={spots}
          onSpotsChange={setSpots}
          unitRotation={formData.unit_rotation}
          brandChoices={brandChoices}
          createSpot={createSpot}
          flightStartDate={formData.flight_start_date}
          flightEndDate={formData.flight_end_date}
        />

        <WeeklyRotationSummary
          spots={spots}
          onSpotsChange={setSpots}
          unitRotation={formData.unit_rotation}
          acknowledgedZeroDays={acknowledgedZeroDays}
          onToggleZeroDay={toggleZeroDayAck}
        />

        {antennaSDActive && (
          <div className={classes.sdBanner}>
            <span>&#9888;&#65039;</span>
            <span>
              <strong>Antenna requires SD.</strong> Include SD has been auto-enabled for all spots when Antenna-SD is a
              destination.
            </span>
          </div>
        )}

        <DestinationsSection
          selectedDestinations={selectedDestinations}
          onDestinationsChange={setSelectedDestinations}
          onAntennaSDChange={handleAntennaSDChange}
        />

        <FormFooter
          onCancel={handleCancel}
          onSubmit={handleSubmit}
          onGeneratePdf={handleGeneratePdf}
          onAttachPdf={handleAttachPdf}
          onRemoveAttachedPdf={handleRemoveAttachedPdf}
          attachedPdf={attachedPdfFile}
          isSubmitting={isSubmitting}
          submitBlocked={submitBlocked}
          submitBlockedReason={submitBlockedReason}
        />
      </div>

      <PDFPreviewModal
        open={pdfModalOpen}
        onClose={() => setPdfModalOpen(false)}
        pdfUrl={pdfUrl}
        onSend={handlePdfSend}
        isLoading={isSubmitting}
      />

      {isSubmitting && (
        <div className={classes.loadingOverlay}>
          <CircularProgress color="inherit" />
          <p style={{ marginTop: 12 }}>Please wait while we process your request.</p>
        </div>
      )}
    </div>
  );
};
